/** A target rectangle, in the scroll container's CONTENT coordinate space
 *  (i.e. measured from the scroll origin, not from the screen). */
export interface RevealRect {
  top: number;
  height: number;
}

/** The scroll container's current vertical state. */
export interface RevealContainer {
  scrollTop: number;
  clientHeight: number;
  /**
   * Total content height, when the caller knows it. Bounds the returned target
   * to a `scrollTop` the container can ACTUALLY reach.
   *
   * It matters only where the target is COMPARED before being applied — with
   * the gutter, a rect in the last {@link REVEAL_GUTTER_PX} of the content asks
   * for more scroll than exists, and `pickQuoteScrollTop` would then rank that
   * occurrence by a travel distance it never pays. Where the target is merely
   * assigned to `element.scrollTop`, the DOM clamps identically and this
   * changes nothing.
   *
   * Optional so a caller reasoning in pure geometry (and every existing test
   * fixture) is unaffected: omit it and only the `>= 0` floor applies.
   */
  scrollHeight?: number;
}

/** The furthest `container` can scroll, or `Infinity` if it hasn't said. */
function maxScrollTop(container: RevealContainer): number {
  if (container.scrollHeight === undefined) return Number.POSITIVE_INFINITY;
  return Math.max(0, container.scrollHeight - container.clientHeight);
}

/**
 * Breathing room left between a revealed rect and the edge it aligns to — CSS
 * `scroll-margin` by hand (STK-2337).
 *
 * Landing a highlight flush against the fold is what "minimal movement" means
 * taken literally, and it reads as clipped: the eye has no run-up into the line.
 * One line of body text (~24px, and the design system's `space-6`) is enough to
 * seat it in the flow; more starts costing real estate on a PDF page for no
 * added legibility.
 *
 * ONE value for every surface on purpose, unlike the hover debounces, which are
 * per-interaction feel. This is layout: there is no story where a mail body
 * wants different breathing room than a PDF band.
 */
export const REVEAL_GUTTER_PX = 24;

/**
 * Vertical `scrollTop` needed to bring `rect` into `container`'s visible box,
 * or `null` when it is ALREADY fully visible — in which case the caller must
 * not move the document a single pixel (STK-1602).
 *
 * Minimal-movement semantics, i.e. CSS `block: "nearest"`: a rect above the
 * viewport aligns to the top, one below aligns to the bottom, and a rect taller
 * than the viewport aligns to the top (its whole height cannot be revealed, and
 * the top is the useful end). Horizontal scrolling is never considered — the
 * delineation resolver always emits full-width bands.
 *
 * Every alignment clears the edge by {@link REVEAL_GUTTER_PX}. The gutter moves
 * the LANDING only — never the visibility gate above it, which still asks the
 * bare question "can the user read this already?". So a rect sitting 3px from
 * the bottom edge does not move (it is readable), while one 3px past it scrolls
 * and lands a gutter clear. Widening the gate instead would scroll for content
 * that is already on screen, which is precisely what STK-2337 set out not to do.
 *
 * `insetTop` shrinks the visible box from the top by that many pixels without
 * moving `container.scrollTop` itself — for a container with an opaque sticky
 * header (ExcelViewer's `<thead className="sticky top-0">`) painted over the
 * first `insetTop` px of the scroll box, a rect sitting there is occluded even
 * though it is technically within `[scrollTop, scrollTop + clientHeight]`.
 * Defaults to 0, which reduces to the original behavior exactly (CustomPDFViewer
 * has no sticky overlay and never passes it).
 *
 * It is clamped to `[0, clientHeight]` because it is MEASURED, not configured
 * (`headerRef.getBoundingClientRect().height`): a collapsed container mid-layout
 * reports `clientHeight: 0` while the header still measures its real height, and
 * an unclamped inset larger than the container would invert the visible box
 * (`visibleTop > visibleBottom`) — every rect would then read as "not visible"
 * and get scrolled to a bogus target. Clamped, the degenerate case simply
 * top-aligns, which is the sane fallback.
 */
export function scrollTopToReveal(
  rect: RevealRect,
  container: RevealContainer,
  insetTop = 0,
): number | null {
  const inset = Math.min(Math.max(insetTop, 0), container.clientHeight);
  const visibleTop = container.scrollTop + inset;
  const visibleBottom = container.scrollTop + container.clientHeight;
  const rectBottom = rect.top + rect.height;

  if (rect.top >= visibleTop && rectBottom <= visibleBottom) return null;

  const reachable = maxScrollTop(container);
  const settle = (target: number) => Math.min(Math.max(0, target), reachable);

  // Above the (inset-adjusted) viewport, overlapping its top edge, or taller
  // than the effective viewable height → align top, a gutter below the inset.
  // The gutter applies to the taller-than-viewport case too: those 24px come off
  // a height that already cannot fit, and a band butting the fold reads as cut.
  if (rect.top < visibleTop || rect.height > container.clientHeight - inset) {
    return settle(rect.top - inset - REVEAL_GUTTER_PX);
  }
  // Below the viewport → align its bottom edge a gutter above the viewport's.
  // A rect in the content's last REVEAL_GUTTER_PX asks for more scroll than
  // exists; it lands flush instead, which is the honest answer and the one
  // `pickQuoteScrollTop` must rank on (STK-2337 follow-up).
  return settle(rectBottom - container.clientHeight + REVEAL_GUTTER_PX);
}

export interface ScrollElementIntoViewOptions {
  /** See {@link scrollTopToReveal}'s `insetTop`. Defaults to 0. */
  insetTop?: number;
}

/** `el`'s live rect in `container`'s content space (see {@link RevealRect}). */
export function contentRect(
  el: HTMLElement,
  container: HTMLElement,
): RevealRect {
  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return {
    top: elRect.top - containerRect.top + container.scrollTop,
    height: elRect.height,
  };
}

/**
 * Scroll `container` vertically so `el` is visible — and ONLY if it is not
 * already. Measures live DOM rects and converts them into the container's
 * content space, so it is correct at any zoom, rotation, or CSS transform
 * (a rotated highlight overlay reports its axis-aligned bounding box, which is
 * exactly what "bring it into view" needs). Shared by CustomPDFViewer's band
 * overlays and ExcelViewer's highlighted rows.
 */
export function scrollElementIntoView(
  el: HTMLElement,
  container: HTMLElement,
  options: ScrollElementIntoViewOptions = {},
): void {
  const next = scrollTopToReveal(
    contentRect(el, container),
    {
      scrollTop: container.scrollTop,
      clientHeight: container.clientHeight,
      scrollHeight: container.scrollHeight,
    },
    options.insetTop,
  );
  if (next !== null) container.scrollTop = next;
}
